import { Deferred } from "@inertiajs/react"

import { StatusBadge } from "@/components/ui/badge"
import { Notice } from "@/components/ui/notice"
import { Panel } from "@/components/ui/panel"

import type { Contact, LiveContact } from "../types"

function Presence({ on, label }: { on: boolean; label: string }) {
  return (
    <span className={on ? "text-secondary" : "text-quaternary line-through"}>
      {label}
    </span>
  )
}

/** Who is reachable, with presence flags; the chosen contact's live channels arrive deferred. */
export function PeoplePanel({
  contacts,
  live,
}: {
  contacts: Contact[]
  live?: LiveContact
}) {
  return (
    <Panel
      title="People"
      description="Presence on file. Numbers come live for the person you would ask for."
    >
      <ul className="divide-secondary flex flex-col divide-y">
        {contacts.map((c) => (
          <li key={c.id} className="flex flex-col gap-1 py-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-primary text-sm font-medium">{c.name}</span>
              {c.title && (
                <span className="text-tertiary text-sm">{c.title}</span>
              )}
              {c.booking_contact && (
                <StatusBadge tier="remote">Books</StatusBadge>
              )}
              {c.do_not_call && (
                <StatusBadge tier="caution">Do not call</StatusBadge>
              )}
              {c.silent && <StatusBadge tier="caution">Silent</StatusBadge>}
            </div>
            <div className="flex flex-wrap gap-3 text-xs">
              <Presence on={c.phone || c.mobile} label="phone" />
              <Presence on={c.email} label="email" />
              <span className="text-tertiary">
                {c.has_booked ? "has booked" : "never booked"}
              </span>
              {c.roles.length > 0 && (
                <span className="text-tertiary">{c.roles.join(", ")}</span>
              )}
            </div>
          </li>
        ))}
        {contacts.length === 0 && (
          <li className="text-tertiary py-2 text-sm">
            Nobody on file. Ring reception for a name.
          </li>
        )}
      </ul>
      <Deferred
        data="live_contact"
        fallback={
          <div
            role="status"
            aria-label="Fetching live contact details"
            className="provisional-slot mt-2 h-[72px]"
          />
        }
      >
        {live &&
          (live.available ? (
            <dl className="border-secondary mt-2 grid grid-cols-2 gap-x-4 gap-y-1 border-t pt-3 text-sm">
              <dt className="text-tertiary text-xs">Phone</dt>
              <dd className="text-secondary">{live.phone ?? "—"}</dd>
              <dt className="text-tertiary text-xs">Mobile</dt>
              <dd className="text-secondary">{live.mobile_phone ?? "—"}</dd>
              <dt className="text-tertiary text-xs">Email</dt>
              <dd className="text-secondary break-all">{live.email ?? "—"}</dd>
            </dl>
          ) : (
            <Notice tone="warning" role="status" className="mt-2">
              Live contact details unavailable; showing what was last synced.
            </Notice>
          ))}
      </Deferred>
    </Panel>
  )
}
